import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { fetchAdminAuditLog, type AdminAuditEntry } from "../../api/admin";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

function formatDetail(detail: unknown): string {
  if (detail == null) return "—";
  try {
    if (typeof detail === "string") return detail;
    return JSON.stringify(detail);
  } catch {
    return "—";
  }
}

export default function AdminAuditPage() {
  const { user } = useAuth();

  const auditQuery = useQuery({
    queryKey: ["admin/audit-log"],
    queryFn: () => fetchAdminAuditLog(100),
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const entries = auditQuery.data?.entries ?? [];

  const gridColumns = "140px minmax(0,1.5fr) 150px minmax(0,2fr)";
  const headerCell: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell title="Audit log">
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: 12,
            padding: "10px 0 8px",
            borderBottom: "1px solid var(--border-subtle)",
            minWidth: 680,
          }}
        >
          <span style={headerCell}>When</span>
          <span style={headerCell}>Admin</span>
          <span style={headerCell}>Action</span>
          <span style={headerCell}>Detail</span>
        </div>

        {auditQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={28} />
            <Skel height={28} />
            <Skel height={28} />
            <Skel height={28} />
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<ScrollText size={20} />}
            title="No audit entries yet"
            hint="Admin actions like plan changes, key creation, and deletions will show up here."
            style={{ margin: "12px 0" }}
          />
        ) : (
          entries.map((entry: AdminAuditEntry) => (
            <div
              key={entry.id}
              style={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 12,
                alignItems: "baseline",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-subtle)",
                minWidth: 680,
              }}
            >
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                {new Date(entry.createdAt).toLocaleString(undefined, {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              <span style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {entry.adminEmail ?? entry.adminUserId}
              </span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-secondary)" }}>
                {entry.action}
              </span>
              <span style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {entry.targetId ? `${entry.targetType} ${entry.targetId.slice(0, 8)} · ` : ""}
                {formatDetail(entry.detail)}
              </span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
